import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useGetBankDepositAvailabilityQuery, useGetBankDepositAccountQuery, useGetBankDepositStepsStatusQuery, useVerifyBvnAddonMutation, useVerifyPhoneMutation, useCreateSubAccountMutation } from '../../features/bank-deposits/bankDepositSlice'
import { Loader } from '../../shared/Loader'
import { DepositHistoryTable } from '../../components/Dashboard/transactions/DepositHistoryTable'
import {
    AiFillCheckCircle,
    AiFillExclamationCircle,
    AiFillBank,
    AiFillCreditCard,
    AiFillPhone,
    AiFillIdcard,
    AiFillWarning
} from 'react-icons/ai'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentUser, updateCredentials } from '../../features/auth/authSlice'
import { useCurrentUserQuery } from '../../features/chat/chatSlice'
import { Link } from 'react-router-dom'

interface StepProps {
    step: number
    title: string
    description: string
    isCompleted: boolean
    isActive: boolean
    icon: React.ReactNode
}

const StepIndicator: React.FC<StepProps> = ({ step, title, description, isCompleted, isActive, icon }) => (
    <div className={`flex items-center space-x-4 p-4 rounded-lg transition-all duration-300 ${isActive ? 'bg-primary/20 border-2 border-primary' :
        isCompleted ? 'bg-green-900/30 border-2 border-green-500' :
            'bg-gray-800 border-2 border-gray-700'
        }`}>
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${isCompleted ? 'bg-green-500 text-white' :
            isActive ? 'bg-primary text-white' :
                'bg-gray-600 text-gray-300'
            }`}>
            {isCompleted ? <AiFillCheckCircle className="w-6 h-6" /> : icon}
        </div>
        <div className="flex-1">
            <h3 className={`font-semibold ${isActive ? 'text-primary' : isCompleted ? 'text-green-400' : 'text-gray-300'}`}>
                Step {step}: {title}
            </h3>
            <p className={`text-sm ${isActive ? 'text-primary/80' : isCompleted ? 'text-green-400' : 'text-gray-400'}`}>
                {description}
            </p>
        </div>
    </div>
)

// The BVN is verified against the user's existing KYC (same person: name +
// date of birth). Three outcomes: approved -> next step; pending -> an admin
// reviews the name match; rejected -> the reason is shown and they may retry.
const BVNVerificationForm: React.FC<{
    onSuccess: (data: any) => void
    onError: (error: string) => void
    isLoading: boolean
    rejectReason?: string | null
}> = ({ onSuccess, onError, isLoading, rejectReason }) => {
    const [bvn, setBvn] = useState('')
    const [consent, setConsent] = useState(false)
    const [verifyBvnAddon, { isLoading: isVerifying }] = useVerifyBvnAddonMutation()
    const busy = isLoading || isVerifying

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!bvn || bvn.length !== 11) {
            onError('Please enter a valid 11-digit BVN')
            return
        }
        if (!consent) {
            onError('Please confirm that this is your own BVN')
            return
        }

        try {
            const result = await verifyBvnAddon({ bvn, isSubjectConsent: true }).unwrap()
            if (result.status === 'approved') {
                onSuccess({ verified: true, source: 'bvn', customerName: '' })
            } else {
                // pending: the refetched steps status switches to the review
                // screen. rejected: show why.
                onError(result.status === 'rejected' ? result.message || 'We could not verify this BVN' : '')
            }
        } catch (error: any) {
            onError(error?.data?.message || 'BVN verification failed')
        }
    }

    return (
        <div className="bg-gray-900 rounded-xl shadow-lg p-8 border border-gray-700">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AiFillIdcard className="w-8 h-8 text-blue-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Verify Your BVN</h2>
                <p className="text-gray-300">
                    Your bank deposit account is opened with your BVN. It must be your own — we check it
                    against your verified identity.
                </p>
            </div>

            {rejectReason && (
                <div className="mb-6 rounded-lg border border-red-800 bg-red-900/20 p-4 text-sm text-red-300">
                    {rejectReason}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="bvn" className="block text-sm font-medium text-gray-300 mb-2">
                        Bank Verification Number (BVN)
                    </label>
                    <input
                        type="text"
                        id="bvn"
                        value={bvn}
                        onChange={(e) => setBvn(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        placeholder="Enter your 11-digit BVN"
                        className="w-full px-4 py-3 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-lg text-center tracking-widest placeholder-gray-400"
                        maxLength={11}
                        disabled={busy}
                    />
                    <p className="text-xs text-gray-400 mt-1">Dial *565*0# on your registered line to see your BVN</p>
                </div>

                <label className="flex items-start gap-3 text-sm text-gray-300">
                    <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-1"
                        disabled={busy}
                    />
                    <span>This is my own BVN and I consent to GCBuying verifying it.</span>
                </label>

                <button
                    type="submit"
                    disabled={busy || bvn.length !== 11 || !consent}
                    className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {busy ? (
                        <div className="flex items-center justify-center">
                            <Loader />
                            <span className="ml-2">Verifying BVN...</span>
                        </div>
                    ) : (
                        'Verify BVN'
                    )}
                </button>
            </form>
        </div>
    )
}

const PhoneVerificationForm: React.FC<{
    onSuccess: (data: any) => void
    onError: (error: string) => void
    isLoading: boolean
    bvnData: any
}> = ({ onSuccess, onError, isLoading, bvnData }) => {
    const [phone, setPhone] = useState('')
    const [verifyPhone] = useVerifyPhoneMutation()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!phone || phone.length < 10) {
            onError('Please enter a valid phone number')
            return
        }

        try {
            const result = await verifyPhone({ phone_no: phone }).unwrap()
            onSuccess(result)
        } catch (error: any) {
            onError(error?.data?.message || 'Phone verification failed')
        }
    }

    return (
        <div className="bg-gray-900 rounded-xl shadow-lg p-8 border border-gray-700">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AiFillPhone className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Verify Your Phone Number</h2>
                <p className="text-gray-300">Enter the phone number linked to your bank account</p>
            </div>

            <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-300 mb-2">Identity Verified:</h3>
                {bvnData?.customerName && (
                    <p className="text-sm text-blue-200">
                        <strong>Name:</strong> {bvnData.customerName}
                    </p>
                )}
                <p className="text-sm text-blue-200">
                    {bvnData?.source === 'kyc'
                        ? 'Verified from your KYC record — no BVN needed'
                        : <><strong>BVN:</strong> {bvnData?.bvn || 'on file'}</>}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number
                    </label>
                    <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                        placeholder="Enter your phone number"
                        className="w-full px-4 py-3 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-lg placeholder-gray-400"
                        disabled={isLoading}
                    />
                </div>

                <button
                    type="submit"
                    disabled={isLoading || phone.length < 10}
                    className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center">
                            <Loader />
                            <span className="ml-2">Verifying Phone...</span>
                        </div>
                    ) : (
                        'Verify Phone Number'
                    )}
                </button>
            </form>
        </div>
    )
}

const CreateAccountForm: React.FC<{
    onSuccess: (data: any) => void
    onError: (error: string) => void
    isLoading: boolean
    verificationData: any
}> = ({ onSuccess, onError, isLoading, verificationData }) => {
    const [createSubAccount] = useCreateSubAccountMutation()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        try {
            const result = await createSubAccount().unwrap()
            onSuccess(result)
        } catch (error: any) {
            onError(error?.data?.message || 'Account creation failed')
        }
    }

    return (
        <div className="bg-gray-900 rounded-xl shadow-lg p-8 border border-gray-700">
            <div className="text-center mb-8">
                <div className="w-16 h-16 bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AiFillCreditCard className="w-8 h-8 text-purple-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Create Deposit Account</h2>
                <p className="text-gray-300">Review your details and create your deposit account</p>
            </div>

            <div className="bg-green-900/30 border border-green-600 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-300 mb-2">Verification Complete:</h3>
                {verificationData?.customerName && (
                    <p className="text-sm text-green-200">
                        <strong>Name:</strong> {verificationData.customerName}
                    </p>
                )}
                <p className="text-sm text-green-200">
                    <strong>Phone:</strong> {verificationData?.phone_no || 'on file'}
                </p>
                <p className="text-sm text-green-200">
                    {verificationData?.bvn ? <><strong>BVN:</strong> {verificationData.bvn}</> : 'Identity verified via KYC'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-4">
                    <div className="flex items-start">
                        <AiFillExclamationCircle className="w-5 h-5 text-yellow-400 mt-0.5 mr-2" />
                        <div>
                            <h4 className="font-semibold text-yellow-300">Important Notice</h4>
                            <p className="text-sm text-yellow-200 mt-1">
                                By creating a deposit account, you agree to our terms and conditions.
                                A PalmPay virtual account will be created for you; transfers to it are credited to your wallet automatically.
                            </p>
                        </div>
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center">
                            <Loader />
                            <span className="ml-2">Creating Account...</span>
                        </div>
                    ) : (
                        'Create Deposit Account'
                    )}
                </button>
            </form>
        </div>
    )
}

const AccountDetails: React.FC<{ account: any }> = ({ account }) => {
    const [currentPage, setCurrentPage] = useState(1)

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    return (
        <div className="space-y-8">
            <div className="bg-black rounded-xl shadow-lg p-8 border border-gray-700">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AiFillBank className="w-8 h-8 text-green-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Your Deposit Account</h2>
                    <p className="text-gray-300">Use these details to make deposits to your account</p>
                </div>

                <div className="space-y-6">
                    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                        <h3 className="font-semibold text-white mb-4">Account Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-400">Account Name</label>
                                <p className="text-lg font-semibold text-white">{account.account_name}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-400">Account Number</label>
                                <p className="text-lg font-semibold text-white font-mono">{account.account_no}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-400">Bank Name</label>
                                <p className="text-lg font-semibold text-white">{account.bank_name}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-400">Reference</label>
                                <p className="text-lg font-semibold text-white font-mono">{account.reference}</p>
                            </div>
                        </div>
                    </div>

                    {/* Payer-name rule: deposits are only auto-credited when the sender's name matches the verified identity. */}
                    <div className="bg-amber-900/30 border border-amber-500 rounded-lg p-4">
                        <div className="flex items-start">
                            <AiFillExclamationCircle className="w-5 h-5 text-amber-400 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-amber-100">
                                <span className="font-semibold text-amber-300">Important:</span> All transfers to this account must originate from a bank account registered in your name. Transfers from third-party accounts or accounts bearing a different name will not be credited.
                            </p>
                        </div>
                    </div>

                    <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4">
                        <div className="flex items-start">
                            <AiFillExclamationCircle className="w-5 h-5 text-blue-400 mt-0.5 mr-2" />
                            <div>
                                <h4 className="font-semibold text-blue-300">How to Deposit</h4>
                                <ul className="text-sm text-blue-200 mt-2 space-y-1">
                                    <li>• Transfer funds to the PalmPay account details above (any Nigerian bank app)</li>
                                    <li>• Use your reference number for easy identification</li>
                                    <li>• Deposits are automatically credited to your wallet</li>
                                    <li>• Contact support if you encounter any issues</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Deposit History Table */}
            <DepositHistoryTable
                page={currentPage}
                onPageChange={handlePageChange}
            />
        </div>
    )
}

export const Deposit: React.FC = () => {
    const dispatch = useDispatch()
    const user = useSelector(selectCurrentUser)
    // Fetch current user once on mount to sync KYC status
    const { data: currentUser } = useCurrentUserQuery()

    // Super-admin switch (`bankDepositsEnabled`). Until it has answered, the
    // feature is treated as off so a slow network never flashes a creation
    // wizard whose every call would 503.
    const { data: availability, isLoading: isAvailabilityLoading } = useGetBankDepositAvailabilityQuery()
    const bankDepositsEnabled = !!availability?.enabled

    const { data: account, isLoading: isAccountLoading, error: accountError } = useGetBankDepositAccountQuery(undefined, { skip: !bankDepositsEnabled })
    // Starting step: KYC identity / captured BVN skips step 1; captured phone skips step 2.
    const { data: stepsStatus } = useGetBankDepositStepsStatusQuery(undefined, { skip: !bankDepositsEnabled || !user?.isKYCVerified })
    const [currentStep, setCurrentStep] = useState(1)
    const [bvnData, setBvnData] = useState<any>(null)
    const [phoneData, setPhoneData] = useState<any>(null)
    const [error, setError] = useState<string>('')
    const [isLoading, setIsLoading] = useState(false)

    // Sync KYC status when it changes
    useEffect(() => {
        if (currentUser && user && user?.isKYCVerified !== currentUser?.isKYCVerified) {
            dispatch(updateCredentials({ user: currentUser }))
        }
    }, [currentUser, user, dispatch])

    // `bvn` comes from the backend; the legacy flags cover an older one.
    const bvnDone = stepsStatus?.bvn
        ? stepsStatus.bvn === 'not_required' || stepsStatus.bvn === 'verified'
        : !!(stepsStatus?.kycIdentity || stepsStatus?.bvnCaptured)
    const bvnUnderReview = stepsStatus?.bvn === 'pending_review'

    useEffect(() => {
        if (!stepsStatus || stepsStatus.hasAccount) return
        if (bvnDone) {
            setBvnData((prev: any) => prev ?? { verified: true, source: stepsStatus.kycIdentity ? 'kyc' : 'bvn', customerName: '' })
            setCurrentStep(2)
        }
        if (stepsStatus.phoneCaptured) {
            setPhoneData((prev: any) => prev ?? { phone_no: '', customerName: '' })
            setCurrentStep(3)
        }
    }, [stepsStatus, bvnDone])

    const handleBVNSuccess = (data: any) => {
        setBvnData(data)
        setCurrentStep(2)
        setError('')
    }

    const handlePhoneSuccess = (data: any) => {
        setPhoneData(data)
        setCurrentStep(3)
        setError('')
    }

    const handleAccountSuccess = (data: any) => {
        setCurrentStep(4)
        setError('')
        // Refresh the account query
        window.location.reload()
    }

    const handleError = (errorMessage: string) => {
        setError(errorMessage)
    }

    if (isAvailabilityLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader />
            </div>
        )
    }

    if (!bankDepositsEnabled) {
        return (
            <div className="min-h-screen py-8">
                <Helmet>
                    <title>Deposit | Dashboard</title>
                    <meta name="description" content="Bank deposits are temporarily unavailable" />
                </Helmet>

                <div className="max-w-6xl mx-auto px-4 flex items-center justify-center min-h-[60vh]">
                    <div className="bg-gray-800/50 border border-white/10 rounded-2xl p-8 max-w-md text-center">
                        <div className="text-5xl mb-4">🏦</div>
                        <p className="text-white/90 text-lg">Bank deposits are temporarily unavailable.</p>
                        <p className="text-white/60 text-sm mt-2">
                            Please check back soon or contact support.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    if (isAccountLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader />
            </div>
        )
    }

    // If account exists, show account details
    if (account && !accountError) {
        return (
            <div className="min-h-screen py-8">
                <Helmet>
                    <title>Deposit Account | Dashboard</title>
                    <meta name="description" content="Your deposit account details" />
                </Helmet>

                <div className="max-w-6xl mx-auto px-4">
                    <AccountDetails account={account} />
                </div>
            </div>
        )
    }

    // If user is not KYC verified, show KYC required message
    if (!user?.isKYCVerified) {
        return (
            <div className="min-h-screen py-8 lg:px-4">
                <Helmet>
                    <title>KYC Required | Dashboard</title>
                    <meta name="description" content="KYC verification required to create deposit account" />
                </Helmet>

                <div className="max-w-2xl mx-auto px-4">
                    <div className="bg-gray-900 rounded-xl shadow-lg p-8 border border-gray-700">
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <AiFillWarning className="w-10 h-10 text-yellow-400" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-4">KYC Verification Required</h1>
                            <p className="text-gray-300 mb-6">
                                To create a bank deposit account, you need to complete your KYC verification first.
                                This helps us ensure the security of your account and comply with regulatory requirements.
                            </p>
                        </div>

                        <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-4 mb-6">
                            <div className="flex items-start">
                                <AiFillExclamationCircle className="w-5 h-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                                <div>
                                    <h4 className="font-semibold text-yellow-300">Why is KYC required?</h4>
                                    <ul className="text-sm text-yellow-200 mt-2 space-y-1">
                                        <li>• Protects your account from unauthorized access</li>
                                        <li>• Complies with financial regulations</li>
                                        <li>• Enables higher transaction limits</li>
                                        <li>• Provides access to all platform features</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* A bank deposit account is opened with a BVN, so verify with it. */}
                        <Link to="/app/account/kyc?doc=bvn">
                            <button
                                type="button"
                                className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                            >
                                Complete KYC Verification
                            </button>
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    // If no account, show creation flow
    return (
        <div className="min-h-screen py-8 lg:px-4">
            <Helmet>
                <title>Create Deposit Account | Dashboard</title>
                <meta name="description" content="Create your deposit account" />
            </Helmet>

            <div className="w-full mx-auto px-4 lg:px-10 py-10 rounded-xl bg-black">
                <div className="my-8">
                    <h1 className="text-3xl font-bold text-white mb-2">Create Deposit Account</h1>
                    <p className="text-gray-300">Follow these steps to create your deposit account</p>
                </div>

                <div className='flex flex-col lg:flex-row justify-between items-start gap-8'>

                    {/* Step Indicators */}
                    <div className="mb-8 space-y-4 w-full lg:w-2/5">
                        <StepIndicator
                            step={1}
                            title="Verify BVN"
                            description="Enter your Bank Verification Number"
                            isCompleted={currentStep > 1}
                            isActive={currentStep === 1}
                            icon={<AiFillIdcard className="w-6 h-6" />}
                        />
                        <StepIndicator
                            step={2}
                            title="Verify Phone"
                            description="Confirm your phone number"
                            isCompleted={currentStep > 2}
                            isActive={currentStep === 2}
                            icon={<AiFillPhone className="w-6 h-6" />}
                        />
                        <StepIndicator
                            step={3}
                            title="Create Account"
                            description="Create your deposit account"
                            isCompleted={currentStep > 3}
                            isActive={currentStep === 3}
                            icon={<AiFillCreditCard className="w-6 h-6" />}
                        />
                    </div>



                    {/* Step Content */}
                    <div className="w-full lg:w-3/5">
                        {/* Error Message */}
                        {error && (
                            <div className="mb-6 bg-red-900/30 border border-red-600 rounded-lg p-4">
                                <div className="flex items-center">
                                    <AiFillExclamationCircle className="w-5 h-5 text-red-400 mr-2" />
                                    <p className="text-red-200">{error}</p>
                                </div>
                            </div>
                        )}
                        {currentStep === 1 && bvnUnderReview && (
                            <div className="bg-gray-900 rounded-xl shadow-lg p-8 border border-gray-700 text-center">
                                <div className="w-16 h-16 bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <AiFillIdcard className="w-8 h-8 text-yellow-400" />
                                </div>
                                <h2 className="text-2xl font-bold text-white mb-2">Your BVN is being reviewed</h2>
                                <p className="text-gray-300">
                                    Part of the name on your BVN differs from your verified identity, so our team is
                                    taking a quick look. We will notify you as soon as it is done — there is nothing
                                    more you need to do.
                                </p>
                            </div>
                        )}
                        {currentStep === 1 && !bvnUnderReview && (
                            <BVNVerificationForm
                                onSuccess={handleBVNSuccess}
                                onError={handleError}
                                isLoading={isLoading}
                                rejectReason={stepsStatus?.bvn === 'rejected' ? stepsStatus.bvnRejectReason : null}
                            />
                        )}

                        {currentStep === 2 && bvnData && (
                            <PhoneVerificationForm
                                onSuccess={handlePhoneSuccess}
                                onError={handleError}
                                isLoading={isLoading}
                                bvnData={bvnData}
                            />
                        )}

                        {currentStep === 3 && phoneData && (
                            <CreateAccountForm
                                onSuccess={handleAccountSuccess}
                                onError={handleError}
                                isLoading={isLoading}
                                verificationData={phoneData}
                            />
                        )}
                    </div>
                </div>

            </div>
        </div>
    )
}
